import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaShieldAlt, FaUserShield, FaLock } from "react-icons/fa";
import { useAdmin } from "../context/AdminContext";
import Alert from "../components/Alert";

export default function AdminLoginPage() {
  const { loginAdmin, adminUser, checkAdminSession, alert, showAlert } = useAdmin();
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: "", password: "" });
  const [err, setErr] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    checkAdminSession();
  }, [checkAdminSession]);

  useEffect(() => {
    if (adminUser?.username) navigate("/3workers_admin_hun_yara/dashboard");
  }, [adminUser, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    
    const u = form.username.trim();
    const p = form.password;

    if (!u || !p) return setErr("Credentials required for uplink.");

    setIsSubmitting(true);
    const ok = await loginAdmin(u, p);
    setIsSubmitting(false);
    
    if (ok) navigate("/3workers_admin_hun_yara/dashboard");
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans relative overflow-hidden flex items-center justify-center px-6">
      
      {/* Background Security Accents */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-indigo-50/40 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50/40 blur-[100px] rounded-full translate-y-1/2 -translate-x-1/2" />

      <Alert
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-md bg-white border border-slate-200 rounded-[3rem] p-10 md:p-12 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)]"
      >
        {/* Security Badge */}
        <div className="text-center mb-10">
          <motion.div 
            animate={{ rotateY: [0, 360] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="w-20 h-20 mx-auto bg-slate-900 text-white rounded-[2rem] flex items-center justify-center mb-6 shadow-2xl shadow-indigo-200"
          >
            <FaShieldAlt size={32} />
          </motion.div>
          <h1 className="text-3xl font-black tracking-tighter uppercase text-slate-900">
            3Workers.cc <span className="text-indigo-600">Admin</span>
          </h1>
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mt-2">
            Secure Command Protocol
          </p>
        </div>

        {err && (
          <motion.div 
            initial={{ opacity: 0, x: -10 }} 
            animate={{ opacity: 1, x: 0 }}
            className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-100 text-rose-600 text-[11px] font-black uppercase tracking-wider text-center"
          >
            {err}
          </motion.div>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Admin Identity */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Admin Identity</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                <FaUserShield size={14} />
              </div>
              <input
                value={form.username}
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none transition-all font-bold text-slate-700"
                placeholder="Identification Name"
              />
            </div>
          </div>

          {/* Secure Access Key */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Access Key</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-600 transition-colors">
                <FaLock size={14} />
              </div>
              <input
                type={showPass ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none transition-all font-bold text-slate-700 tracking-widest"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-indigo-600 transition-colors"
              >
                {showPass ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
          </div>

          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            disabled={isSubmitting}
            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-indigo-600 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {isSubmitting ? "Syncing..." : "Establish Connection"}
          </motion.button>
        </form>

        {/* Footer Security Note */}
        <div className="mt-10 pt-8 border-t border-slate-100 text-center">
          <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest leading-relaxed">
            Authorized Personnel Only <br />
     
          </p>
        </div>
      </motion.div>
    </div>
  );
}